import { apiGateway } from '@va/api-client';
import { getInstanceId } from '@va/dashboard/selectors/app';
import { getConfig } from '@va/standalone/shared/api-client';
import { WithRequired } from '@va/types/util';
import { toQueryString } from '@va/util/helpers';
import { SWRResponse, useAsyncFunction, useFetchData } from '@va/util/hooks';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ConversionEventsResponse, ConversionEventValueType } from './conversions.types';

export const useAddNote = (conversionId: string | null) => {
  const websiteId = useSelector(getInstanceId);
  const asyncFunc = async (note: string) => {
    if (!conversionId) return;
    return await apiGateway.post(
      `v1/websites/${websiteId}/conversion-events/${conversionId}/update`,
      { note },
      getConfig(),
    );
  };
  return useAsyncFunction(asyncFunc);
};

export const useConversionEventsByKind = (
  shouldFetchData = true,
  pagination = {
    page: 1,
    pageSize: 10,
  },
  filter: Record<string, unknown>,
  controller: AbortController,
): SWRResponse<{ items: WithRequired<ConversionEventValueType, 'id'>[]; total: number }, Error> => {
  const websiteId = useSelector(getInstanceId);

  const queryData = useMemo(
    () => ({
      ...pagination,
    }),
    [pagination],
  );

  const url = useMemo(
    () => `v1/websites/${websiteId}/conversion-events/list?${toQueryString(queryData)}`,
    [queryData, websiteId],
  );

  const body = useMemo(() => {
    const filtersArray = Object.entries(filter).reduce(
      (acc, [key, value]) => {
        if (value != null && value !== '') {
          acc.push({ member: key, operator: 'in', values: [value] });
        }
        return acc;
      },
      [] as Array<{ member: string; operator: string; values: unknown[] }>,
    );

    return { where: { and: filtersArray } };
  }, [filter]);

  const fetcher = useCallback(
    async (url: string, body: any) => {
      return await apiGateway.post(url, body, { ...getConfig(), signal: controller.signal });
    },
    [controller.signal],
  );

  const mapper = useCallback((data: ConversionEventsResponse) => {
    return {
      items: data.payload ?? [],
      total: data.meta.total,
    };
  }, []);

  return useFetchData<{ items: WithRequired<ConversionEventValueType, 'id'>[]; total: number }, Error>(
    shouldFetchData ? [url, body] : null,
    {
      revalidateOnFocus: false,
    },
    mapper,
    fetcher,
  );
};

export const useConversionAuthors = () => {
  const websiteId = useSelector(getInstanceId);

  const url = useMemo(() => `/v2/websites/${websiteId}/conversion-events/stats/authors`, []);

  const fetcher = useCallback(async (url: string) => apiGateway.get(url), []);

  const mapper = useCallback((response: { payload: string[] }) => response.payload, []);

  return useFetchData([url], {}, mapper, fetcher);
};

export const getConversionEventData = (websiteId: string, conversionEventId: string) => {
  return apiGateway.get<{ meta: {}; payload: ConversionEventValueType }>(
    `v1/websites/${websiteId}/conversion-events/${conversionEventId}`,
    getConfig(),
  );
};
